/**
 * Cited-tier merge of available + unavailable sources (#20423 close-gaps,
 * contract §document_unavailable).
 *
 * A cited chunk the requester can't resolve (deleted or unauthorized —
 * deliberately indistinguishable) still occupies its citation slot: the
 * backend emits it in `unavailable_sources` with its stable 1-based
 * `citation_index`, and the payload's `assistant_response_sources` order
 * already accounts for those gaps. This module rebuilds the full numbered
 * sequence so inline `[N]` markers, `source-N` anchors, and the Sources panel
 * all agree — numbering NEVER shifts when a document disappears.
 *
 * Pure module (no React) so the merge is unit-testable.
 */
import type {
  ProvenanceSource,
  ProvenanceUnavailableSource,
} from "../../types/provenance";

/** One slot in the merged Cited-tier sequence. */
export type CitedTierEntry =
  | { kind: "source"; citationNumber: number; source: ProvenanceSource }
  | {
      kind: "unavailable";
      /** Null for an unavailable entry the backend couldn't index (defensive);
       * such rows render "Document unavailable" without a number, at the end. */
      citationNumber: number | null;
      entry: ProvenanceUnavailableSource;
    };

/**
 * Walk citation numbers 1..N, slotting each unavailable entry at its
 * `citation_index` and filling every other slot with the next available cited
 * source (payload order). Unavailable entries without a citation_index are
 * appended unnumbered. Read-tier (`cited: false`) unavailable entries are the
 * caller's concern — pass only the cited ones.
 */
export const mergeCitedWithUnavailable = (
  citedSources: ProvenanceSource[],
  unavailableCited: ProvenanceUnavailableSource[],
): CitedTierEntry[] => {
  const indexed = new Map<number, ProvenanceUnavailableSource>();
  const unindexed: ProvenanceUnavailableSource[] = [];
  for (const entry of unavailableCited) {
    if (entry.citation_index != null && !indexed.has(entry.citation_index)) {
      indexed.set(entry.citation_index, entry);
    } else {
      // A null index OR a duplicate of an already-claimed index — keep it as an
      // unnumbered row rather than dropping it silently (#20423 L6).
      unindexed.push(entry);
    }
  }

  const merged: CitedTierEntry[] = [];
  let nextAvailable = 0;
  let citationNumber = 1;
  while (nextAvailable < citedSources.length || indexed.size > 0) {
    const unavailable = indexed.get(citationNumber);
    if (unavailable) {
      indexed.delete(citationNumber);
      merged.push({ kind: "unavailable", citationNumber, entry: unavailable });
    } else if (nextAvailable < citedSources.length) {
      merged.push({
        kind: "source",
        citationNumber,
        source: citedSources[nextAvailable],
      });
      nextAvailable += 1;
    } else {
      // Only stranded unavailable indices remain (defensive: an index past the
      // sequence). Emit them in index order at their claimed numbers.
      const stranded = [...indexed.entries()].sort(([a], [b]) => a - b);
      for (const [n, entry] of stranded) {
        merged.push({ kind: "unavailable", citationNumber: n, entry });
      }
      indexed.clear();
      break;
    }
    citationNumber += 1;
  }

  for (const entry of unindexed) {
    merged.push({ kind: "unavailable", citationNumber: null, entry });
  }
  return merged;
};
